import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../../../../../contexts/CurrentUserContext";
import "./VacationList.css";

const VacationList = ({ vacations = [], daysOff = [], dateRange }) => {
  const { userData } = useContext(UserContext);
  const [showMyDepartmentsOnly, setShowMyDepartmentsOnly] = useState(false);

  // Функция для определения типа отпуска и соответствующего класса
  const getVacationTypeClass = (type) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes("учебн")) {
      return "vacation-list__vacation-type--study";
    } else if (typeLower.includes("отпуск") && typeLower.includes("без содержания")) {
      return "vacation-list__vacation-type--unpaid";
    } else if (typeLower.includes("отпуск")) {
      return "vacation-list__vacation-type--vacation";
    } else if (typeLower.includes("больничн")) {
      return "vacation-list__vacation-type--sick";
    } else if (typeLower.includes("отгул")) {
      return "vacation-list__vacation-type--dayoff";
    } else if (typeLower.includes("командировк")) {
      return "vacation-list__vacation-type--business-trip";
    }
    return "";
  };

  // Функция для определения класса элемента таблицы в зависимости от типа отпуска
  const getVacationItemClass = (type) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes("учебн")) {
      return "vacation-list__item--study";
    } else if (typeLower.includes("отпуск") && typeLower.includes("без содержания")) {
      return "vacation-list__item--unpaid";
    } else if (typeLower.includes("отпуск")) {
      return "vacation-list__item--vacation";
    } else if (typeLower.includes("больничн")) {
      return "vacation-list__item--sick";
    } else if (typeLower.includes("отгул")) {
      return "vacation-list__item--dayoff";
    } else if (typeLower.includes("командировк")) {
      return "vacation-list__item--business-trip";
    }
    return "";
  };

  // Функция для определения приоритета типа отсутствия
  const getVacationTypePriority = (type) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes("учебн")) return 2;
    if (typeLower.includes("отпуск") && !typeLower.includes("без содержания"))
      return 1;
    if (typeLower.includes("отпуск") && typeLower.includes("без содержания"))
      return 3;
    if (typeLower.includes("больничн")) return 4;
    if (typeLower.includes("отгул")) return 5;
    if (typeLower.includes("командировк")) return 6;
    return 7;
  };

  // Фильтрация по периоду
  const { startDate, endDate } = dateRange;
  const periodStart = new Date(startDate);
  periodStart.setHours(0, 0, 0, 0);
  const periodEnd = new Date(endDate);
  periodEnd.setHours(23, 59, 59, 999);

  // Фильтруем отпуска и больничные: если период отпуска пересекается с выбранным периодом
  const filteredVacations = vacations.filter((v) => {
    const start = new Date(v.period_start);
    const end = new Date(v.period_end);
    return end >= periodStart && start <= periodEnd;
  });

  // Группируем по user+type
  const groupedVacations = {};
  filteredVacations.forEach((vacation) => {
    const key = `${vacation.user}-${vacation.type}`;
    if (!groupedVacations[key]) {
      groupedVacations[key] = {
        ...vacation,
        dates: [],
      };
    }
    // Добавляем все даты отпуска в периоде
    const start = new Date(vacation.period_start);
    const end = new Date(vacation.period_end);
    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      // Только если дата в выбранном периоде
      if (d >= periodStart && d <= periodEnd) {
        groupedVacations[key].dates.push(new Date(d));
      }
    }
  });

  // Фильтруем отгулы по периоду
  const filteredDaysOff = daysOff.filter((d) => {
    const date = new Date(d.date_of_use || d.date);
    date.setHours(0, 0, 0, 0);
    return date >= periodStart && date <= periodEnd;
  });

  // Готовим список для отображения
  const allItems = [
    ...Object.values(groupedVacations).map((v) => ({ ...v, isDayOff: false })),
    ...filteredDaysOff.map((d) => ({
      user: d.user,
      group: d.department || d.group || "",
      type: "Отгул",
      dates: [new Date(d.date_of_use || d.date)],
      time: d.time,
      isDayOff: true,
    })),
  ];

  // Фильтруем по отделам пользователя, если включен чекбокс
  const filteredItems = allItems.filter((item) => {
    if (!showMyDepartmentsOnly) return true;

    // Для отпусков используем department, для отгулов - group
    const userDepartment = item.isDayOff ? item.group : item.department;

    // Если у пользователя есть группы, проверяем принадлежность
    if (userData && userData.departments) {
      const allGroups = userData.departments.flatMap(
        (dept) => dept.groups || []
      );
      if (allGroups.length > 0) {
        const myDepartmentNames = allGroups.map((group) => group.name);
        return myDepartmentNames.includes(userDepartment);
      }
    }

    return true; // Если у пользователя нет групп, показываем всех
  });

  // Группируем по типу
  const grouped = filteredItems.reduce((acc, item) => {
    const type = item.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(item);
    return acc;
  }, {});

  // Сортируем внутри каждой группы по дате начала
  Object.keys(grouped).forEach((type) => {
    grouped[type].sort((a, b) => {
      const dateA = a.dates[0];
      const dateB = b.dates[0];
      return dateA - dateB;
    });
  });

  // Преобразуем в массив и сортируем группы по приоритету типа
  const groupedData = Object.entries(grouped)
    .sort(
      ([typeA], [typeB]) =>
        getVacationTypePriority(typeA) - getVacationTypePriority(typeB)
    )
    .map(([type, items]) => ({
      type,
      items,
    }));

  // Группируем по отделам для лучшей структуры
  const groupByDepartment = (items) => {
    const departments = {};

    items.forEach((item) => {
      const department = item.isDayOff ? item.group : item.department;
      const departmentName = department || "Не указан отдел";

      if (!departments[departmentName]) {
        departments[departmentName] = [];
      }
      departments[departmentName].push(item);
    });

    // Сортируем отделы по алфавиту
    return Object.entries(departments)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([departmentName, items]) => ({
        departmentName,
        items: items.sort((a, b) => {
          // Сортируем сотрудников по имени
          return a.user.localeCompare(b.user);
        }),
      }));
  };

  // Определяем текст для чекбокса в зависимости от количества отделов
  const getCheckboxLabel = () => {
    if (!userData || !userData.departments) return "Мой отдел";
    const allGroups = userData.departments.flatMap((dept) => dept.groups || []);
    if (allGroups.length === 1) {
      return "Мой отдел";
    } else if (allGroups.length > 1) {
      return "Мои отделы";
    }
    return "Мой отдел";
  };

  return (
    <div className="vacation-list">
      <div className="vacation-list__header">
        <h3 className="vacation-list__title">Отпуска и отгулы</h3>
        {userData && userData.groups && userData.groups.length > 0 && (
          <label className="vacation-list__filter">
            <input
              type="checkbox"
              checked={showMyDepartmentsOnly}
              onChange={(e) => setShowMyDepartmentsOnly(e.target.checked)}
              className="vacation-list__checkbox"
            />
            <span className="vacation-list__checkbox-label">
              {getCheckboxLabel()}
            </span>
          </label>
        )}
      </div>
      <div className="vacation-list__content">
        {groupedData.length === 0 ? (
          <div className="vacation-list__empty">
            Нет отпусков и отгулов за этот период
          </div>
        ) : (
          groupedData.map(({ type, items }) => {
            const departments = groupByDepartment(items);

            return (
              <div key={type} className="vacation-list__group">
                <h4 className="vacation-list__group-title">{type}</h4>
                {departments.map(
                  ({ departmentName, items: departmentItems }) => (
                    <div
                      key={departmentName}
                      className="vacation-list__department"
                    >
                      <h5 className="vacation-list__department-title">
                        {departmentName}
                      </h5>
                      {departmentItems.map((item, index) => {
                        // Сортируем даты
                        const sortedDates = [...item.dates].sort(
                          (a, b) => a - b
                        );

                        // Находим начало и конец периода
                        const startDate = sortedDates[0];
                        const endDate = sortedDates[sortedDates.length - 1];

                        // Форматируем даты
                        const formatDate = (date) => {
                          return date.toLocaleDateString("ru-RU", {
                            day: "numeric",
                            month: "long",
                          });
                        };

                        let periodStr = formatDate(startDate);
                        if (startDate.getTime() !== endDate.getTime()) {
                          periodStr = `${formatDate(startDate)} - ${formatDate(
                            endDate
                          )}`;
                        }

                        return (
                          <div
                            key={`${type}-${departmentName}-${index}`}
                            className={`vacation-list__item ${getVacationItemClass(
                              item.type
                            )}`}
                          >
                            <div className="vacation-list__user-info">
                              <span className="vacation-list__user-name">
                                {item.user}
                              </span>
                            </div>
                            <div className="vacation-list__vacation-info">
                              <span
                                className={`vacation-list__vacation-type ${getVacationTypeClass(
                                  item.type
                                )}`}
                              >
                                {item.isDayOff
                                  ? `Отгул (${Math.floor(item.time / 3600)} ч.)`
                                  : item.type}
                              </span>
                              <span className="vacation-list__vacation-dates">
                                {periodStr}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VacationList;
